import { animated, useSprings } from '@react-spring/web';
import { useDrag } from '@use-gesture/react';
import classNames from 'classnames';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import clamp from '../../../utils/clamp';

import useStyles from './styles';
import ICarousel from './types';

function Carousel({
  className,
  classNameImage,
  items,
  current = 0,
  showIndicator = true,
  onChange,
}: ICarousel) {
  const classes = useStyles();

  const swipeContainerRef = useRef<HTMLDivElement>();

  const [width, setWidth] = useState<number>(0);
  const [currentIndex, setCurrentIndex] = useState<number>(current);

  useLayoutEffect(() => {
    setWidth(swipeContainerRef.current?.clientWidth || 0);
  }, []);

  const [springProps, api] = useSprings(
    items.length,
    (i) => ({
      x: (i - currentIndex) * (width - 24),
      display: 'flex',
      zIndex: i === currentIndex ? 1 : 0,
    }),
    [width],
  );

  const runAnimation = useCallback(
    (active?: boolean, movX?: number) => {
      api.start((i) => {
        if (i < currentIndex - 1 || i > currentIndex + 1) {
          return { display: 'none' };
        }

        const x = (i - currentIndex) * (width - 24) + (active ? movX : 0);
        return {
          x,
          display: 'flex',
          zIndex: i === currentIndex ? 1 : 0,
        };
      });
    },
    [api, currentIndex, width],
  );

  useEffect(() => {
    if (onChange) {
      onChange(currentIndex);
    }
    runAnimation();
  }, [
    currentIndex,
    onChange,
    // eslint-disable-next-line
    // @ts-ignore
    runAnimation,
  ]);

  useEffect(() => {
    setCurrentIndex(current);
  }, [current]);

  const bindSwipeGesture = useDrag(({ active, movement: [movX], direction: [dirX], cancel }) => {
    if (active && Math.abs(movX) > width / 2) {
      setCurrentIndex((prev) => clamp(prev + (dirX > 0 ? -1 : 1), 0, items.length - 1));
      cancel();
    }

    runAnimation(active, movX);
  }) as unknown as () => void; // types are broken, this IS a function

  return (
    <div className={className}>
      <div className={classNames(classes.root)} ref={swipeContainerRef}>
        {springProps.map(({ x, display, zIndex }, i) => (
          <animated.div
            className={classes.page}
            {...bindSwipeGesture()}
            key={i}
            style={{ display, x, zIndex }}
          >
            <div
              className={classNames(classes.image, classNameImage)}
              style={{
                backgroundImage: `url(${items[i].src})`,
              }}
              role="img"
              aria-label={items[i].alt}
            ></div>
            {items[i].content && <div className={classes.content}>{items[i].content}</div>}
          </animated.div>
        ))}
      </div>

      {showIndicator && (
        <div className={classes.activeIndicatorBack}>
          <div
            className={classes.activeIndicatorFront}
            style={{
              width: `${100 / items.length}%`,
              marginLeft: `${currentIndex * (100 / items.length)}%`,
            }}
          ></div>
        </div>
      )}
    </div>
  );
}

export default Carousel;
